/* tslint:disable:max-classes-per-file */
import { APIHandler } from "../apiHandler";

const removeInlineComments = (text: string) =>
  text.replace(/^\s*\/\/.*$/gm, "");

const safeParseJson = (text: string) => JSON.parse(removeInlineComments(text));

/** The current theme is set via put and tracked in this variable. */
let currentTheme = "JupyterLab Light";

// tslint:disable-next-line:max-classes-per-file
class ThemesHandler extends APIHandler {
  async put() {
    try {
      const json = safeParseJson(await this.request.text());
      currentTheme = json.theme;
    } catch (err) {
      // tslint:disable-next-line: no-console
      console.warn("Error parsing theme from PUT request.", err);
    }
    // https://github.com/jpillora/xhook/issues/116
    // 204 is required by JupyterLab.
    // Locally I usually edit /node_modules/xhook/dist/xhook.js to get it working.
    this.finishResponse(new Response(null, { status: 204 }));
  }
  async get() {
    this.setHeader("Content-Type", "application/json");
    this.finish({
      id: "@jupyterlab/apputils-extension:themes",
      raw: `{\n    // Theme\n    // @jupyterlab/apputils-extension:themes\n    // Theme manager settings.\n    // *************************************\n\n    // Selected Theme\n    // Application-level visual styling theme\n    "theme": "${currentTheme}"\n}`,
      schema: {
        title: "Theme",
        "jupyter.lab.setting-icon-label": "Theme Manager",
        description: "Theme manager settings.",
        type: "object",
        additionalProperties: false,
        definitions: {
          cssOverrides: {
            type: "object",
            additionalProperties: false,
            description:
              "The description field of each item is the CSS property that will be used to validate an override's value",
            properties: {
              "code-font-size": {
                type: ["string", "null"],
                description: "font-size",
              },
              "content-font-size1": {
                type: ["string", "null"],
                description: "font-size",
              },
              "ui-font-size1": {
                type: ["string", "null"],
                description: "font-size",
              },
            },
          },
        },
        properties: {
          theme: {
            type: "string",
            title: "Selected Theme",
            description: "Application-level visual styling theme",
            default: currentTheme,
          },
          "theme-scrollbars": {
            type: "boolean",
            title: "Scrollbar Theming",
            description: "Enable/disable styling of the application scrollbars",
            default: false,
          },
          overrides: {
            title: "Theme CSS Overrides",
            description:
              "Override theme CSS variables by setting key-value pairs here",
            $ref: "#/definitions/cssOverrides",
            default: {
              "code-font-size": null,
              "content-font-size1": null,
              "ui-font-size1": null,
            },
          },
        },
      },
      settings: { theme: currentTheme },
      version: "1.2.4",
    });
  }
}

// tslint:disable-next-line:max-classes-per-file
class SettingsHandler extends APIHandler {
  async get() {
    this.setHeader("Content-Type", "application/json");
    this.finish({
      settings: [
        {
          id: "@jupyter-widgets/jupyterlab-manager:plugin",
          raw: "{}",
          schema: {
            title: "Jupyter Widgets",
            description: "Jupyter widgets settings.",
            additionalProperties: false,
            properties: {
              saveState: {
                type: "boolean",
                title: "Save Jupyter widget state in notebooks",
                description:
                  "Automatically save Jupyter widget state when a notebook is saved.",
                default: false,
              },
            },
            type: "object",
          },
          settings: {},
          version: "1.1.0",
        },
        {
          id: "@jupyterlab/application-extension:main",
          raw: "{}",
          schema: {
            title: "Application ",
            description: "Main JupyterLab application settings.",
            "jupyter.lab.setting-icon-label": "Application",
            "jupyter.lab.shortcuts": [
              {
                command: "application:activate-next-tab",
                keys: ["Ctrl Shift ]"],
                selector: "body",
              },
              {
                command: "application:activate-previous-tab",
                keys: ["Ctrl Shift ["],
                selector: "body",
              },
              {
                command: "application:close",
                keys: ["Alt W"],
                selector: ".jp-Activity",
              },
              {
                command: "application:toggle-mode",
                keys: ["Accel Shift D"],
                selector: "body",
              },
              {
                command: "application:toggle-left-area",
                keys: ["Accel B"],
                selector: "body",
              },
              {
                command: "application:toggle-right-area",
                keys: [""],
                selector: "body",
              },
              {
                command: "application:toggle-presentation-mode",
                keys: [""],
                selector: "body",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/application-extension:sidebar",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-label": "Sidebar",
            title: "Sidebar",
            description: "Sidebar layout settings.",
            properties: {
              overrides: {
                type: "object",
                title: "Overrides",
                default: {},
                description:
                  'Overrides for where to show sidebar items\ne.g., {"tab-manager": "right"}',
                properties: {},
                additionalProperties: {
                  type: "string",
                  enum: ["left", "right"],
                },
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/apputils-extension:palette",
          raw: "{}",
          schema: {
            title: "Command Palette",
            description: "Command palette settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "apputils:activate-command-palette",
                keys: ["Accel Shift C"],
                selector: "body",
              },
            ],
            additionalProperties: false,
            properties: {},
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/apputils-extension:print",
          raw: "{}",
          schema: {
            title: "Print",
            description: "Print settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "apputils:print",
                keys: ["Accel P"],
                selector: "body",
              },
            ],
            additionalProperties: false,
            properties: {},
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },

        // http://localhost:9090/static/dist/jupyterlab/lab/api/settings/@jupyterlab/apputils-extension:themes?1603999971715
        {
          id: "@jupyterlab/apputils-extension:themes",
          raw: "{}",
          schema: {
            title: "Theme",
            "jupyter.lab.setting-icon-label": "Theme Manager",
            description: "Theme manager settings.",
            type: "object",
            additionalProperties: false,
            definitions: {
              cssOverrides: {
                type: "object",
                additionalProperties: false,
                description:
                  "The description field of each item is the CSS property that will be used to validate an override's value",
                properties: {
                  "code-font-size": {
                    type: ["string", "null"],
                    description: "font-size",
                  },
                  "content-font-size1": {
                    type: ["string", "null"],
                    description: "font-size",
                  },
                  "ui-font-size1": {
                    type: ["string", "null"],
                    description: "font-size",
                  },
                },
              },
            },
            properties: {
              theme: {
                type: "string",
                title: "Selected Theme",
                description: "Application-level visual styling theme",
                default: currentTheme,
              },
              "theme-scrollbars": {
                type: "boolean",
                title: "Scrollbar Theming",
                description:
                  "Enable/disable styling of the application scrollbars",
                default: false,
              },
              overrides: {
                title: "Theme CSS Overrides",
                description:
                  "Override theme CSS variables by setting key-value pairs here",
                $ref: "#/definitions/cssOverrides",
                default: {
                  "code-font-size": null,
                  "content-font-size1": null,
                  "ui-font-size1": null,
                },
              },
            },
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/codemirror-extension:commands",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class": "jp-TextEditorIcon",
            "jupyter.lab.setting-icon-label": "CodeMirror",
            title: "CodeMirror",
            description: "Text editor settings for all CodeMirror editors.",
            properties: {
              keyMap: {
                type: "string",
                title: "Key Map",
                description: "Configures the keymap to use",
                default: "default",
              },
              theme: {
                type: "string",
                title: "Theme",
                description:
                  "CSS file defining the corresponding\n.cm-s-[name] styles is loaded",
                default: "jupyter",
              },
              scrollPastEnd: {
                type: "boolean",
                title: "Scroll behavior",
                description: "Whether to scroll past the end of text document",
                default: true,
              },
              recordTiming: {
                type: "boolean",
                title: "Record cell timing",
                description: "Whether to record cell timing or not.",
                default: true,
              },
              styleActiveLine: {
                type: ["boolean", "object"],
                title: "Style Active Line",
                description: "Value is boolean, or { nonEmpty: boolean }",
                default: false,
              },
              styleSelectedText: {
                type: "boolean",
                title: "Style Selected Text",
                description: "Value is boolean",
                default: true,
              },
              selectionPointer: {
                type: ["boolean", "string"],
                title: "Selection Pointer",
                description: "Value is boolean or string, e.g. 'pointer'",
                default: false,
              },
            },
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/completer-extension:consoles",
          raw: "{}",
          schema: {
            title: "Console Completer",
            description: "Console completer settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "completer:invoke-console",
                keys: ["Tab"],
                selector:
                  ".jp-CodeConsole-promptCell .jp-mod-completer-enabled",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/completer-extension:files",
          raw: "{}",
          schema: {
            title: "File Editor Completer",
            description: "File editor completer settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "completer:invoke-file",
                keys: ["Tab"],
                selector: ".jp-FileEditor .jp-mod-completer-enabled",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/completer-extension:notebooks",
          raw: "{}",
          schema: {
            title: "Notebook Completer",
            description: "Notebook completer settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "completer:invoke-notebook",
                keys: ["Tab"],
                selector:
                  ".jp-Notebook.jp-mod-editMode .jp-mod-completer-enabled",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/console-extension:tracker",
          raw: "{}",
          schema: {
            title: "Code Console",
            description: "Code Console settings.",
            "jupyter.lab.setting-icon-class": "jp-CodeConsoleIcon",
            "jupyter.lab.setting-icon-label": "Code Console Settings",
            "jupyter.lab.shortcuts": [
              {
                command: "console:linebreak",
                keys: ["Enter"],
                selector:
                  ".jp-CodeConsole[data-jp-interaction-mode='notebook'] .jp-CodeConsole-promptCell",
              },
              {
                command: "console:run-forced",
                keys: ["Shift Enter"],
                selector:
                  ".jp-CodeConsole[data-jp-interaction-mode='notebook'] .jp-CodeConsole-promptCell",
              },
              {
                command: "console:linebreak",
                keys: ["Accel Enter"],
                selector:
                  ".jp-CodeConsole[data-jp-interaction-mode='terminal'] .jp-CodeConsole-promptCell",
              },
              {
                command: "console:run-forced",
                keys: ["Shift Enter"],
                selector:
                  ".jp-CodeConsole[data-jp-interaction-mode='terminal'] .jp-CodeConsole-promptCell",
              },
              {
                command: "console:run-unforced",
                keys: ["Enter"],
                selector:
                  ".jp-CodeConsole[data-jp-interaction-mode='terminal'] .jp-CodeConsole-promptCell",
              },
            ],
            properties: {
              interactionMode: {
                title: "Interaction mode",
                description:
                  "Whether the console interaction mimics the notebook\nor terminal keyboard shortcuts.",
                type: "string",
                enum: ["notebook", "terminal"],
                default: "notebook",
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/docmanager-extension:plugin",
          raw: "{}",
          schema: {
            title: "Document Manager",
            description: "Document Manager settings.",
            "jupyter.lab.setting-icon-class": "jp-FileIcon",
            "jupyter.lab.setting-icon-label": "Document Manager",
            "jupyter.lab.transform": true,
            "jupyter.lab.shortcuts": [
              {
                command: "docmanager:save",
                keys: ["Accel S"],
                selector: "body",
              },
              {
                command: "docmanager:save-as",
                keys: ["Accel Shift S"],
                selector: "body",
              },
            ],
            properties: {
              autosave: {
                type: "boolean",
                title: "Autosave Documents",
                description: "Whether to autosave documents",
                default: true,
              },
              autosaveInterval: {
                type: "number",
                title: "Autosave Interval",
                description: "Length of save interval in seconds",
                default: 120,
              },
              defaultViewers: {
                type: "object",
                title: "Default Viewers",
                default: {},
                description: "Overrides for the default viewers for file types",
                properties: {},
                additionalProperties: { type: "string" },
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/documentsearch-extension:plugin",
          raw: "{}",
          schema: {
            title: "Document Search",
            description: "Document search plugin.",
            "jupyter.lab.shortcuts": [
              {
                command: "documentsearch:start",
                keys: ["Accel F"],
                selector: ".jp-mod-searchable",
              },
              {
                command: "documentsearch:highlightNext",
                keys: ["Accel G"],
                selector: ".jp-mod-searchable",
              },
              {
                command: "documentsearch:highlightPrevious",
                keys: ["Accel Shift G"],
                selector: ".jp-mod-searchable",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/extensionmanager-extension:plugin",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class": "jp-SettingsIcon",
            "jupyter.lab.setting-icon-label": "Extension Manager",
            title: "Extension Manager",
            description: "Extension manager settings.",
            properties: {
              enabled: {
                title: "Enabled Status",
                description:
                  "Enables extension manager (requires Node.js/npm).\nWARNING: installing untrusted extensions may be unsafe.",
                default: false,
                type: "boolean",
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/filebrowser-extension:browser",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class": "jp-FolderIcon",
            "jupyter.lab.setting-icon-label": "File Browser",
            title: "File Browser",
            description: "File Browser settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "filebrowser:create-main-launcher",
                keys: ["Accel Shift L"],
                selector: "body",
              },
              {
                command: "filebrowser:toggle-main",
                keys: ["Accel Shift F"],
                selector: "body",
              },
            ],
            properties: {
              navigateToCurrentDirectory: {
                type: "boolean",
                title: "Navigate to current directory",
                description:
                  "Whether to automatically navigate to a document's current directory",
                default: false,
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/fileeditor-extension:plugin",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class": "jp-TextEditorIcon",
            "jupyter.lab.setting-icon-label": "Editor",
            title: "Text Editor",
            description: "Text editor settings.",
            definitions: {
              editorConfig: {
                properties: {
                  autoClosingBrackets: { type: "boolean" },
                  fontFamily: { type: ["string", "null"] },
                  fontSize: {
                    type: ["integer", "null"],
                    minimum: 1,
                    maximum: 100,
                  },
                  lineHeight: { type: ["number", "null"] },
                  lineNumbers: { type: "boolean" },
                  lineWrap: {
                    type: "string",
                    enum: ["off", "on", "wordWrapColumn", "bounded"],
                  },
                  matchBrackets: { type: "boolean" },
                  readOnly: { type: "boolean" },
                  insertSpaces: { type: "boolean" },
                  tabSize: { type: "number" },
                  wordWrapColumn: { type: "integer" },
                  rulers: { type: "array", items: { type: "number" } },
                  codeFolding: { type: "boolean" },
                },
                additionalProperties: false,
                type: "object",
              },
            },
            properties: {
              editorConfig: {
                title: "Editor Configuration",
                description:
                  "The configuration for all text editors.\nIf `fontFamily`, `fontSize` or `lineHeight` are `null`,\nvalues from current theme are used.",
                $ref: "#/definitions/editorConfig",
                default: {
                  autoClosingBrackets: true,
                  fontFamily: null,
                  fontSize: null,
                  lineHeight: null,
                  lineNumbers: true,
                  lineWrap: "on",
                  matchBrackets: true,
                  readOnly: false,
                  insertSpaces: true,
                  tabSize: 4,
                  wordWrapColumn: 80,
                  rulers: [],
                  codeFolding: false,
                },
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/git:plugin",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class": "jp-GitIcon",
            "jupyter.lab.setting-icon-label": "Git",
            title: "Git",
            description: "jupyterlab-git settings.",
            type: "object",
            properties: {
              cancelPullMergeConflict: {
                type: "boolean",
                title: "Cancel pull merge conflict",
                description:
                  "If true, when fetching and integrating changes from a remote repository, a conflicting merge is canceled and the working tree left untouched.",
                default: false,
              },
              disableBranchWithChanges: {
                type: "boolean",
                title: "Disable branch with changes",
                description:
                  "Disable all branch operations (new, switch) when there are changed/staged files",
                default: false,
              },
              historyCount: {
                type: "integer",
                title: "History count",
                description:
                  "Number of (most recent) commits shown in the history log",
                default: 25,
              },
              refreshInterval: {
                type: "integer",
                title: "Refresh interval",
                description:
                  "Number of milliseconds between polling the file system for changes.",
                default: 3000,
              },
              simpleStaging: {
                type: "boolean",
                title: "Simple staging flag",
                description:
                  "If true, use a simplified concept of staging. Only files with changes are shown (instead of showing staged/changed/untracked), and all files with changes will be automatically staged",
                default: false,
              },
            },
          },
          settings: {},
          version: "0.10.0",
        },
        {
          id: "@jupyterlab/imageviewer-extension:plugin",
          raw: "{}",
          schema: {
            title: "Image Viewer",
            description: "Image viewer settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "imageviewer:flip-horizontal",
                keys: ["H"],
                selector: ".jp-ImageViewer",
              },
              {
                command: "imageviewer:flip-vertical",
                keys: ["V"],
                selector: ".jp-ImageViewer",
              },
              {
                command: "imageviewer:invert-colors",
                keys: ["I"],
                selector: ".jp-ImageViewer",
              },
              {
                command: "imageviewer:reset-image",
                keys: ["0"],
                selector: ".jp-ImageViewer",
              },
              {
                command: "imageviewer:rotate-clockwise",
                keys: ["]"],
                selector: ".jp-ImageViewer",
              },
              {
                command: "imageviewer:rotate-counterclockwise",
                keys: ["["],
                selector: ".jp-ImageViewer",
              },
              {
                command: "imageviewer:zoom-in",
                keys: ["="],
                selector: ".jp-ImageViewer",
              },
              {
                command: "imageviewer:zoom-out",
                keys: ["-"],
                selector: ".jp-ImageViewer",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/inspector-extension:inspector",
          raw: "{}",
          schema: {
            title: "Inspector",
            description: "Inspector settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "inspector:open",
                keys: ["Accel I"],
                selector: "body",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/launcher-extension:plugin",
          raw: "{}",
          schema: {
            title: "Launcher",
            description: "Launcher settings.",
            "jupyter.lab.shortcuts": [
              { command: "launcher:create", keys: [""], selector: "body" },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/logconsole-extension:plugin",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class": "jp-ListIcon",
            "jupyter.lab.setting-icon-label": "Log Console",
            title: "Log Console",
            description: "Log Console settings.",
            properties: {
              maxLogEntries: {
                type: "number",
                title: "Log entry count limit",
                description: "Maximum number of log entries to store in memory",
                default: 1000,
              },
              flash: {
                type: "boolean",
                title: "Status Bar Item flash",
                description: "Whether to flash on new log message or not",
                default: true,
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.0.6",
        },
        {
          id: "@jupyterlab/mainmenu-extension:plugin",
          raw: "{}",
          schema: {
            title: "Main Menu",
            description: "Main JupyterLab menu settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "editmenu:clear-all",
                keys: [""],
                selector: "[data-jp-undoer]",
              },
              {
                command: "editmenu:clear-current",
                keys: [""],
                selector: "[data-jp-undoer]",
              },
              {
                command: "editmenu:find",
                keys: [""],
                selector: "[data-jp-undoer]",
              },
              {
                command: "editmenu:find-and-replace",
                keys: [""],
                selector: "[data-jp-undoer]",
              },
              {
                command: "editmenu:redo",
                keys: ["Accel Shift Z"],
                selector: "[data-jp-undoer]",
              },
              {
                command: "editmenu:undo",
                keys: ["Accel Z"],
                selector: "[data-jp-undoer]",
              },
              {
                command: "filemenu:close-and-cleanup",
                keys: ["Ctrl Shift Q"],
                selector: ".jp-Activity",
              },
              {
                command: "kernelmenu:interrupt",
                keys: ["I", "I"],
                selector: "[data-jp-kernel-user]:focus",
              },
              {
                command: "kernelmenu:restart",
                keys: ["0", "0"],
                selector: "[data-jp-kernel-user]:focus",
              },
              {
                command: "kernelmenu:restart-and-clear",
                keys: [""],
                selector: "[data-jp-kernel-user]:focus",
              },
              {
                command: "kernelmenu:shutdown",
                keys: [""],
                selector: "[data-jp-kernel-user]:focus",
              },
              {
                command: "runmenu:restart-and-run-all",
                keys: [""],
                selector: "[data-jp-code-runner]",
              },
              {
                command: "runmenu:run",
                keys: ["Shift Enter"],
                selector: "[data-jp-code-runner]",
              },
              {
                command: "runmenu:run-all",
                keys: [""],
                selector: "[data-jp-code-runner]",
              },
              {
                command: "tabsmenu:activate-previously-used-tab",
                keys: ["Accel Shift '"],
                selector: "body",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/markdownviewer-extension:plugin",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class": "jp-MarkdownIcon",
            "jupyter.lab.setting-icon-label": "Markdown Viewer",
            title: "Markdown Viewer",
            description: "Markdown viewer settings.",
            definitions: {
              fontFamily: { type: ["string", "null"] },
              fontSize: { type: ["integer", "null"], minimum: 1, maximum: 100 },
              lineHeight: { type: ["number", "null"] },
              lineWidth: { type: ["number", "null"] },
              hideFrontMatter: { type: "boolean" },
              renderTimeout: { type: "number" },
            },
            properties: {
              fontFamily: {
                title: "Font Family",
                description:
                  "The font family used to render markdown.\nIf `null`, value from current theme is used.",
                $ref: "#/definitions/fontFamily",
                default: null,
              },
              fontSize: {
                title: "Font Size",
                description:
                  "The size in pixel of the font used to render markdown.\nIf `null`, value from current theme is used.",
                $ref: "#/definitions/fontSize",
                default: null,
              },
              lineHeight: {
                title: "Line Height",
                description:
                  "The line height used to render markdown.\nIf `null`, value from current theme is used.",
                $ref: "#/definitions/lineHeight",
                default: null,
              },
              lineWidth: {
                title: "Line Width",
                description:
                  "The text line width expressed in CSS ch units.\nIf `null`, lines fit the viewport width.",
                $ref: "#/definitions/lineWidth",
                default: null,
              },
              hideFrontMatter: {
                title: "Hide Front Matter",
                description:
                  "Whether to hide YAML front matter.\nThe YAML front matter must be placed at the top of the document,\nstarted by a line of three dashes (---) and ended by a line of\nthree dashes (---) or three points (...).",
                $ref: "#/definitions/hideFrontMatter",
                default: true,
              },
              renderTimeout: {
                title: "Render Timeout",
                description: "The render timeout in milliseconds.",
                $ref: "#/definitions/renderTimeout",
                default: 1000,
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/notebook-extension:tracker",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class": "jp-NotebookIcon",
            "jupyter.lab.setting-icon-label": "Notebook",
            "jupyter.lab.shortcuts": [
              {
                command: "notebook:change-cell-to-code",
                keys: ["Y"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:change-cell-to-heading-1",
                keys: ["1"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:change-cell-to-heading-2",
                keys: ["2"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:change-cell-to-heading-3",
                keys: ["3"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:change-cell-to-heading-4",
                keys: ["4"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:change-cell-to-heading-5",
                keys: ["5"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:change-cell-to-heading-6",
                keys: ["6"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:change-cell-to-markdown",
                keys: ["M"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:change-cell-to-raw",
                keys: ["R"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:copy-cell",
                keys: ["C"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:cut-cell",
                keys: ["X"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:delete-cell",
                keys: ["D", "D"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:enter-command-mode",
                keys: ["Escape"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:enter-command-mode",
                keys: ["Ctrl M"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:enter-edit-mode",
                keys: ["Enter"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:extend-marked-cells-above",
                keys: ["Shift ArrowUp"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:extend-marked-cells-above",
                keys: ["Shift K"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:extend-marked-cells-top",
                keys: ["Shift Home"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:extend-marked-cells-below",
                keys: ["Shift ArrowDown"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:extend-marked-cells-bottom",
                keys: ["Shift End"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:extend-marked-cells-below",
                keys: ["Shift J"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:insert-cell-above",
                keys: ["A"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:insert-cell-below",
                keys: ["B"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:merge-cells",
                keys: ["Shift M"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:move-cursor-down",
                keys: ["ArrowDown"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:move-cursor-down",
                keys: ["J"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:move-cursor-up",
                keys: ["ArrowUp"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:move-cursor-up",
                keys: ["K"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:paste-cell-below",
                keys: ["V"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:redo-cell-action",
                keys: ["Shift Z"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:run-cell",
                keys: ["Ctrl Enter"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:run-cell",
                keys: ["Ctrl Enter"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:run-cell-and-insert-below",
                keys: ["Alt Enter"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:run-cell-and-insert-below",
                keys: ["Alt Enter"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:run-in-console",
                keys: [""],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:run-cell-and-select-next",
                keys: ["Shift Enter"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "viewmenu:line-numbering",
                keys: [""],
                selector: ".jp-Notebook.jp-mod-commandMode",
              },
              {
                command: "viewmenu:match-brackets",
                keys: [""],
                selector: ".jp-Notebook.jp-mod-commandMode",
              },
              {
                command: "notebook:select-all",
                keys: ["Accel A"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:split-cell-at-cursor",
                keys: ["Ctrl Shift -"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:toggle-all-cell-line-numbers",
                keys: ["Shift L"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:toggle-cell-line-numbers",
                keys: ["L"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:undo-cell-action",
                keys: ["Z"],
                selector: ".jp-Notebook:focus",
              },
            ],
            title: "Notebook",
            description: "Notebook settings.",
            definitions: {
              editorConfig: {
                properties: {
                  autoClosingBrackets: { type: "boolean" },
                  fontFamily: { type: ["string", "null"] },
                  fontSize: {
                    type: ["integer", "null"],
                    minimum: 1,
                    maximum: 100,
                  },
                  lineHeight: { type: ["number", "null"] },
                  lineNumbers: { type: "boolean" },
                  lineWrap: {
                    type: "string",
                    enum: ["off", "on", "wordWrapColumn", "bounded"],
                  },
                  matchBrackets: { type: "boolean" },
                  readOnly: { type: "boolean" },
                  insertSpaces: { type: "boolean" },
                  tabSize: { type: "number" },
                  wordWrapColumn: { type: "integer" },
                  rulers: { type: "array", items: { type: "number" } },
                  codeFolding: { type: "boolean" },
                },
                additionalProperties: false,
                type: "object",
              },
            },
            properties: {
              codeCellConfig: {
                title: "Code Cell Configuration",
                description: "The configuration for all code cells.",
                $ref: "#/definitions/editorConfig",
                default: {
                  autoClosingBrackets: true,
                  fontFamily: null,
                  fontSize: null,
                  lineHeight: null,
                  lineNumbers: false,
                  lineWrap: "off",
                  matchBrackets: true,
                  readOnly: false,
                  insertSpaces: true,
                  tabSize: 4,
                  wordWrapColumn: 80,
                  rulers: [],
                  codeFolding: false,
                },
              },
              defaultCell: {
                title: "Default cell type",
                description:
                  "The default type (markdown, code, or raw) for new cells",
                type: "string",
                enum: ["code", "markdown", "raw"],
                default: "code",
              },
              kernelShutdown: {
                title: "Shut down kernel",
                description:
                  "Whether to shut down or not the kernel when closing a notebook.",
                type: "boolean",
                default: false,
              },
              markdownCellConfig: {
                title: "Markdown Cell Configuration",
                description: "The configuration for all markdown cells.",
                $ref: "#/definitions/editorConfig",
                default: {
                  autoClosingBrackets: false,
                  fontFamily: null,
                  fontSize: null,
                  lineHeight: null,
                  lineNumbers: false,
                  lineWrap: "on",
                  matchBrackets: false,
                  readOnly: false,
                  insertSpaces: true,
                  tabSize: 4,
                  wordWrapColumn: 80,
                  rulers: [],
                  codeFolding: false,
                },
              },
              rawCellConfig: {
                title: "Raw Cell Configuration",
                description: "The configuration for all raw cells.",
                $ref: "#/definitions/editorConfig",
                default: {
                  autoClosingBrackets: false,
                  fontFamily: null,
                  fontSize: null,
                  lineHeight: null,
                  lineNumbers: false,
                  lineWrap: "on",
                  matchBrackets: false,
                  readOnly: false,
                  insertSpaces: true,
                  tabSize: 4,
                  wordWrapColumn: 80,
                  rulers: [],
                  codeFolding: false,
                },
              },
              scrollPastEnd: {
                title: "Scroll past last cell",
                description:
                  "Whether to be able to scroll so the last cell is at the top of the panel",
                type: "boolean",
                default: true,
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/settingeditor-extension:plugin",
          raw: "{}",
          schema: {
            title: "Setting Editor",
            description: "Setting editor settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "settingeditor:open",
                keys: ["Accel ,"],
                selector: "body",
              },
              {
                command: "settingeditor:save",
                keys: ["Accel S"],
                selector: ".jp-SettingEditor",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/shortcuts-extension:plugin",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-deprecated": true,
            "jupyter.lab.setting-icon-class": "jp-KeyboardIcon",
            "jupyter.lab.setting-icon-label": "Keyboard Shortcuts",
            title: "Keyboard Shortcuts",
            description: "Keyboard shortcut settings for JupyterLab.",
            properties: {},
            oneOf: [{ $ref: "#/definitions/shortcut" }],
            type: "object",
            definitions: {
              shortcut: {
                properties: {
                  command: { type: "string" },
                  keys: {
                    items: { type: "string" },
                    minItems: 1,
                    type: "array",
                  },
                  selector: { type: "string" },
                },
                type: "object",
              },
            },
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/shortcuts-extension:shortcuts",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class": "jp-KeyboardIcon",
            "jupyter.lab.setting-icon-label": "Keyboard Shortcuts",
            "jupyter.lab.transform": true,
            title: "Keyboard Shortcuts",
            description: "Keyboard shortcut settings.",
            type: "object",
            additionalProperties: false,
            properties: {
              shortcuts: {
                title: "Keyboard Shortcuts",
                description: "The list of keyboard shortcuts.",
                items: { $ref: "#/definitions/shortcut" },
                type: "array",
                default: [],
              },
            },
            definitions: {
              shortcut: {
                properties: {
                  args: { type: "object" },
                  command: { type: "string" },
                  keys: {
                    items: { type: "string" },
                    minItems: 1,
                    type: "array",
                  },
                  selector: { type: "string" },
                },
                required: ["command", "keys", "selector"],
                type: "object",
              },
            },
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/statusbar-extension:plugin",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class": "jp-SettingsIcon",
            "jupyter.lab.setting-icon-label": "Status Bar",
            title: "Status Bar",
            description: "Status Bar settings.",
            properties: {
              visible: {
                type: "boolean",
                title: "Status Bar Visibility",
                description: "Whether to show status bar or not",
                default: false,
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/terminal-extension:plugin",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class": "jp-TerminalIcon",
            "jupyter.lab.setting-icon-label": "Terminal",
            title: "Terminal",
            description: "Terminal settings.",
            definitions: {
              fontFamily: { type: "string" },
              fontSize: { type: "integer", minimum: 9, maximum: 72 },
              lineHeight: { type: "number", minimum: 1.0 },
              theme: { enum: ["dark", "light", "inherit"] },
              scrollback: { type: "number" },
              pasteWithCtrlV: { type: "boolean" },
            },
            properties: {
              fontFamily: {
                title: "Font family",
                description: "The font family used to render text.",
                $ref: "#/definitions/fontFamily",
                default: "monospace",
              },
              fontSize: {
                title: "Font size",
                description: "The font size used to render text.",
                $ref: "#/definitions/fontSize",
                default: 13,
              },
              lineHeight: {
                title: "Line height",
                description: "The line height used to render text.",
                $ref: "#/definitions/lineHeight",
                default: 1.0,
              },
              theme: {
                title: "Theme",
                description: "The theme for the terminal.",
                $ref: "#/definitions/theme",
                default: "inherit",
              },
              screenReaderMode: {
                title: "Screen Reader Mode",
                description:
                  "Add accessibility elements for use with screen readers.",
                type: "boolean",
                default: false,
              },
              scrollback: {
                title: "Scrollback Buffer",
                description: "The amount of scrollback beyond initial viewport",
                $ref: "#/definitions/lineHeight",
                default: 1000,
              },
              shutdownOnClose: {
                title: "Shut down on close",
                description: "Shut down the session when closing the terminal.",
                type: "boolean",
                default: false,
              },
              pasteWithCtrlV: {
                title: "Paste with Ctrl+V",
                description:
                  "Enable pasting with Ctrl+V.  This can be disabled to use Ctrl+V in the vi editor, for instance.  This setting has no effect on macOS, where Cmd+V is available",
                type: "boolean",
                default: true,
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.4",
        },
        {
          id: "@jupyterlab/tooltip-extension:consoles",
          raw: "{}",
          schema: {
            title: "Console Tooltips",
            description: "Console tooltip settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "tooltip:dismiss",
                keys: ["Escape"],
                selector: "body.jp-mod-tooltip .jp-CodeConsole-promptCell",
              },
              {
                command: "tooltip:launch-console",
                keys: ["Shift Tab"],
                selector:
                  ".jp-CodeConsole-promptCell .jp-InputArea-editor:not(.jp-mod-has-primary-selection):not(.jp-mod-in-leading-whitespace)",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/tooltip-extension:files",
          raw: "{}",
          schema: {
            title: "File Editor Tooltips",
            description: "File editor tooltip settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "tooltip:launch-file",
                keys: ["Shift Tab"],
                selector: ".jp-FileEditor",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "@jupyterlab/tooltip-extension:notebooks",
          raw: "{}",
          schema: {
            title: "Notebook Tooltips",
            description: "Notebook tooltip settings.",
            "jupyter.lab.shortcuts": [
              {
                command: "tooltip:dismiss",
                keys: ["Escape"],
                selector: "body.jp-mod-tooltip .jp-Notebook",
              },
              {
                command: "tooltip:launch-notebook",
                keys: ["Shift Tab"],
                selector:
                  ".jp-Notebook.jp-mod-editMode .jp-InputArea-editor:not(.jp-mod-has-primary-selection):not(.jp-mod-in-leading-whitespace)",
              },
            ],
            properties: {},
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.2.5",
        },
        {
          id: "nbdime-jupyterlab:plugin",
          raw: "{}",
          schema: {
            "jupyter.lab.setting-icon-class":
              "jp-Icon-16 nbdime-icon fa fa-clock-o",
            "jupyter.lab.setting-icon-label": "Nbdime",
            title: "Nbdime",
            description: "Settings for the nbdime extension.",
            properties: {
              hideUnchanged: {
                title: "Hide Unchanged Cells",
                description:
                  "Whether unchanged cells should be hidden by default.",
                type: "boolean",
                default: true,
              },
            },
            additionalProperties: false,
            type: "object",
          },
          settings: {},
          version: "1.0.0",
        },
      ],
    });
  }
}

// tslint:disable-next-line:max-classes-per-file
class DocManagerHandler extends APIHandler {
  async get() {
    this.setHeader("Content-Type", "application/json");
    this.finish({
      id: "@jupyterlab/docmanager-extension:plugin",
      raw: "{}",
      schema: {
        title: "Document Manager",
        description: "Document Manager settings.",
        "jupyter.lab.setting-icon-class": "jp-FileIcon",
        "jupyter.lab.setting-icon-label": "Document Manager",
        "jupyter.lab.transform": true,
        "jupyter.lab.shortcuts": [
          { command: "docmanager:save", keys: ["Accel S"], selector: "body" },
          {
            command: "docmanager:save-as",
            keys: ["Accel Shift S"],
            selector: "body",
          },
        ],
        properties: {
          autosave: {
            type: "boolean",
            title: "Autosave Documents",
            description: "Whether to autosave documents",
            default: true,
          },
          autosaveInterval: {
            type: "number",
            title: "Autosave Interval",
            description: "Length of save interval in seconds",
            default: 120,
          },
          defaultViewers: {
            type: "object",
            title: "Default Viewers",
            default: {},
            description: "Overrides for the default viewers for file types",
            properties: {},
            additionalProperties: { type: "string" },
          },
        },
        additionalProperties: false,
        type: "object",
      },
      settings: {},
      version: "1.2.4",
    });
  }
}

// tslint:disable-next-line: max-classes-per-file
class WorkspacesHandler extends APIHandler {
  async put() {
    // https://github.com/jpillora/xhook/issues/116
    // 204 is required by JupyterLab.
    // Locally I usually edit /node_modules/xhook/dist/xhook.js to get it working.
    this.finishResponse(new Response(null, { status: 204 }));
  }

  async get() {
    this.finish({
      data: {
        "layout-restorer:data": {
          main: {
            dock: {
              type: "tab-area",
              currentIndex: 0,
              widgets: ["notebook:__notebook_source__.ipynb"],
            },
            mode: "single-document",
            current: "notebook:__notebook_source__.ipynb",
          },
          left: {
            collapsed: true,
            widgets: [
              "filebrowser",
              "running-sessions",
              "git-sessions",
              "command-palette",
              "tab-manager",
            ],
          },
          right: { collapsed: true, widgets: [] },
        },
        "notebook:__notebook_source__.ipynb": {
          data: { path: "__notebook_source__.ipynb", factory: "Notebook" },
        },
      },
      metadata: { id: "/lab" },
    });
  }
}

let shortcuts = {};

class ShortcutsHandler extends APIHandler {
  async put() {
    const json = safeParseJson(await this.request.text());
    shortcuts = json;
    // https://github.com/jpillora/xhook/issues/116
    // 204 is required by JupyterLab.
    // Locally I usually edit /node_modules/xhook/dist/xhook.js to get it working.
    this.finishResponse(new Response(null, { status: 204 }));
  }

  async get() {
    this.setHeader("Content-Type", "application/json");
    const data = {
      id: "@jupyterlab/shortcuts-extension:shortcuts",
      raw: "",
      schema: {
        "jupyter.lab.setting-icon-class": "jp-KeyboardIcon",
        "jupyter.lab.setting-icon-label": "Keyboard Shortcuts",
        "jupyter.lab.transform": true,
        title: "Keyboard Shortcuts",
        description: "Keyboard shortcut settings.",
        type: "object",
        additionalProperties: false,
        properties: {
          shortcuts: {
            title: "Keyboard Shortcuts",
            description: "The list of keyboard shortcuts.",
            items: { $ref: "#/definitions/shortcut" },
            type: "array",
            default: [],
          },
        },
        definitions: {
          shortcut: {
            properties: {
              args: { type: "object" },
              command: { type: "string" },
              keys: {
                items: { type: "string" },
                minItems: 1,
                type: "array",
              },
              selector: { type: "string" },
            },
            required: ["command", "keys", "selector"],
            type: "object",
          },
        },
      },
      settings: {
        ...shortcuts,
      },
      version: "1.2.4",
    };
    data.raw = JSON.stringify(data.settings);
    this.finish(data);
  }
}

// tslint:disable-next-line: max-classes-per-file
class MetricsHandler extends APIHandler {
  async get() {
    // Respond with fake metrics so that this handler doesn't 404 forever.
    // This is just supposed to show JupyterLab memory usage, it's not important to the UI for us.
    this.finish({
      rss: 111501312,
      limits: { memory: { rss: 2147483648, warn: false } },
    });
  }
}

export const defaultHandlers: [string, typeof APIHandler][] = [
  ["/lab/api/workspaces/lab", WorkspacesHandler],
  ["/lab/api/workspaces/lab/workspaces/:rest", WorkspacesHandler], // Support non-default workspaces.
  ["/lab/api/settings/", SettingsHandler],
  [
    "/lab/api/settings/@jupyterlab/shortcuts-extension:shortcuts",
    ShortcutsHandler,
  ],
  ["/lab/api/settings/@jupyterlab/apputils-extension:themes", ThemesHandler],
  [
    "/lab/api/settings/@jupyterlab/docmanager-extension:plugin",
    DocManagerHandler,
  ],
  ["/metrics", MetricsHandler],
];
